import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PolicyModel } from 'src/app/models/api/governance/policy.model';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import * as _ from 'lodash';

@Component({
  selector: 'app-add-location-dialog',
  templateUrl: './add-location-dialog.component.html',
  styleUrl: './add-location-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddLocationDialogComponent implements OnInit {
  public waitingForResponse = false;
  public addLocationsFormGroup: UntypedFormGroup;

  private readonly _maxLocationsToAdd = 500;
  private readonly _destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<AddLocationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public policyData: PolicyModel,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _notificationService: NotificationsService
  ) {}

  public get locationsToAdd(): UntypedFormArray {
    return this.addLocationsFormGroup.get('locationsToAdd') as UntypedFormArray;
  }

  public getLocationErrorMessage(control: AbstractControl): string {
    if (!control || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return 'Location URL is required.';
    }

    if (control.errors['invalidDomain']) {
      return 'Invalid domain. URL must be from SharePoint (.sharepoint.com) or OneDrive (-my.sharepoint.com)';
    }

    if (control.errors['invalidPath']) {
      return 'Incomplete path. SharePoint paths require /sites/{sitename} and OneDrive paths require /personal/{user}/Documents or shared link format /:f:/g/personal/{user}/{token}';
    }

    if (control.errors['duplicate']) {
      return 'This location overlaps with an existing location in the sensitive locations list';
    }

    return '';
  }

  ngOnInit(): void {
    this.addLocationsFormGroup = this._formBuilder.group({
      locationsToAdd: this._formBuilder.array([
        this._formBuilder.group({
          location: this._formBuilder.control('', [
            Validators.required,
            this.createSharePointOneDriveValidator(),
            this.createDuplicateValidator(),
          ]),
        }),
      ]),
    });

    // Revalidate all location fields when any one changes to detect duplicates within the form
    this.locationsToAdd.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.locationsToAdd.controls.forEach((control) => {
        const locationControl = control.get('location');
        if (locationControl) {
          locationControl.updateValueAndValidity({ emitEvent: false });
        }
      });
    });
  }

  private createSharePointOneDriveValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const url = control.value.trim();

      const hasSharePointDomain = url.includes('.sharepoint.com');
      const hasOneDriveDomain = url.includes('-my.sharepoint.com');

      if (!hasSharePointDomain && !hasOneDriveDomain) {
        return { invalidDomain: true };
      }

      if (hasOneDriveDomain) {
        return this._validateOneDrive(url);
      } else if (hasSharePointDomain) {
        return this._validateSharePoint(url);
      }

      return null;
    };
  }

  private _validateOneDrive(url: string): ValidationErrors | null {
    // Check if it's a OneDrive shared link pattern: /:f:/g/personal/{user_identifier}/{token}
    const oneDriveSharedLinkPattern = /:f:\/g\/personal\/[^/]+\/[^/]+/;
    const isSharedLink = oneDriveSharedLinkPattern.test(url);

    // OneDrive URL - must have /personal/ and either /Documents or be a shared link
    if (!url.includes('/personal/')) {
      return { invalidPath: true };
    }

    if (!isSharedLink && !url.includes('/Documents')) {
      return { invalidPath: true };
    }

    return null;
  }

  private _validateSharePoint(url: string): ValidationErrors | null {
    // SharePoint URL - must have /sites/ with sitename after it
    if (!url.includes('/sites/')) {
      return { invalidPath: true };
    }

    const sitesIndex = url.indexOf('/sites/');
    const afterSites = url.substring(sitesIndex + '/sites/'.length);

    if (!afterSites || afterSites.length === 0) {
      return { invalidPath: true };
    }

    return null;
  }

  private createDuplicateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const newLocation = this._normalizeUrl(control.value.toLowerCase().trim());

      // Check against existing locations in policy
      if (this.policyData?.config?.sensitiveLocations) {
        const existingLocations = this.policyData.config.sensitiveLocations.map((loc) =>
          this._normalizeUrl(loc.toLowerCase().trim())
        );

        for (const existingLocation of existingLocations) {
          if (this._locationsOverlap(newLocation, existingLocation)) {
            return { duplicate: true };
          }
        }
      }

      // Check against other locations being added in the form
      const locationsArray = this.locationsToAdd;
      if (locationsArray && locationsArray.length > 0) {
        for (let i = 0; i < locationsArray.length; i++) {
          const otherControl = locationsArray.at(i).get('location');

          if (otherControl === control) {
            continue;
          }

          const otherValue = otherControl?.value;
          if (otherValue && otherValue.trim()) {
            const otherLocation = this._normalizeUrl(otherValue.toLowerCase().trim());
            if (this._locationsOverlap(newLocation, otherLocation)) {
              return { duplicate: true };
            }
          }
        }
      }

      return null;
    };
  }

  private _locationsOverlap(location1: string, location2: string): boolean {
    if (location1 === location2) {
      return true;
    }

    return location1.startsWith(location2 + '/') || location2.startsWith(location1 + '/');
  }

  private _normalizeUrl(url: string): string {
    // Remove trailing slash for consistent comparison
    return url.endsWith('/') ? url.slice(0, -1) : url;
  }

  public addMoreClicked() {
    const locations = this.locationsToAdd;
    if (locations.length < this._maxLocationsToAdd) {
      locations.push(
        this._formBuilder.group({
          location: this._formBuilder.control('', [
            Validators.required,
            this.createSharePointOneDriveValidator(),
            this.createDuplicateValidator(),
          ]),
        })
      );
    } else {
      this._notificationService.warning(`Maximum ${this._maxLocationsToAdd} locations allowed`);
    }
  }

  public removeLocation(indexToRemove: number) {
    if (indexToRemove >= 0 && indexToRemove < this.locationsToAdd.length) {
      this.locationsToAdd.removeAt(indexToRemove);
    }
  }

  public addLocations() {
    if (!this.addLocationsFormGroup.valid) {
      return;
    }

    const locationsList = _.map(this.addLocationsFormGroup.value.locationsToAdd, 'location').filter(
      (location) => location && location.trim()
    );

    this.dialogRef.close({
      newLocations: locationsList,
    });
  }

  public onClose(): void {
    this.dialogRef.close();
  }
}
